import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Plus, Minus, Mail, Phone, Instagram } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTicker from '../components/BrandTicker';
import { API } from '../context/AuthContext';
import { embedSrcFor, parseInstagramUrl, cleanInstagramUrl } from './Campaigns';

const RHEA_IMG = '/about/rhea.jpg';

const SERVICES = [
  { title: 'Brand Strategy & Identity', desc: 'Defining how brands look, feel and grow.' },
  { title: 'Celebrity & Influencer Marketing', desc: 'The right partnerships, every time.' },
  { title: 'Talent Representation for Brand Work', desc: 'Exclusive talent management for digital and branded collaborations.' },
  { title: 'Content Strategy', desc: 'Content with purpose.' },
  { title: 'Campaigns & Experiences', desc: 'Ideas brought to life.' },
  { title: 'The Batch Drop', desc: 'Your content system, simplified.' },
];

const BATCH_STEPS = [
  'Fill out our onboarding form',
  'We plan your content strategy',
  'One shoot day to cover it all',
  'One final content drop, tailored',
  'You just schedule + post',
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <HeroSection />
      <div className="py-5 md:py-6">
        <BrandTicker tone="dark" />
      </div>
      <AboutSection />
      <TopCampaigns />
      <ServicesSection />
      <ProductsBanner />
      <BatchDropSection />
      <ContactSection />
    </div>
  );
}

/* ---------------- HERO ---------------- */
function HeroSection() {
  const STATS = [
    { k: '11+', v: 'Years of Experience' },
    { k: '85+', v: 'Brands Partnered With', italic: true },
    { k: '200+', v: 'Celebrity & Influencer Network' },
    { k: '100+', v: 'Campaigns Executed', italic: true },
  ];
  return (
    <section id="home" className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-12 scroll-mt-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
        <div className="md:col-span-8">
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="text-xs tracking-[0.3em] uppercase text-[#3a5a4e] mb-6 ml-2 md:ml-4">
            Co Crate Studio by Rhea Gupta
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="font-serif text-[16vw] md:text-[10vw] leading-[0.85] tracking-tightest text-[#1a3a2e]">
            strategy.<br />
            <span className="italic font-light">systems.</span><br />
            <span className="font-script text-[#8b5a2b] text-[14vw] md:text-[9vw] inline-block -rotate-2 mt-2">execution.</span>
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-10 max-w-xl text-base md:text-lg text-[#3a5a4e] leading-relaxed">
            Building brands and representing talent through strategy-led content,
            meaningful partnerships and campaigns designed to create impact.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
            className="mt-10 flex flex-wrap items-stretch gap-4">
            <a href="#contact" onClick={(e) => smoothTo(e, 'contact')} data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 border border-[#1a3a2e] bg-[#1a3a2e] text-[#f5f1e8] text-sm tracking-wide hover:bg-[#122a21] transition-colors">
              Let's get brewing <ArrowUpRight size={16} />
            </a>
            <Link to="/campaigns" data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 border border-[#1a3a2e] text-[#1a3a2e] text-sm tracking-wide hover:bg-[#1a3a2e] hover:text-[#f5f1e8] transition-colors">
              View campaigns
            </Link>
          </motion.div>
        </div>

        <motion.aside
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          data-testid="hero-stats"
          className="md:col-span-4 md:pt-10 md:pl-10 md:border-l border-[#1a3a2e]/15"
        >
          {STATS.map((s, i) => (
            <div
              key={s.v}
              data-testid={`hero-stat-${i}`}
              className={`py-5 ${i < STATS.length - 1 ? 'border-b border-[#1a3a2e]/15' : ''}`}
            >
              <p className={`font-serif text-5xl md:text-6xl leading-none text-[#1a3a2e] ${s.italic ? 'italic font-light' : ''}`}>
                {s.k}
              </p>
              <p className="mt-2 text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#3a5a4e]">
                {s.v}
              </p>
            </div>
          ))}
        </motion.aside>
      </div>
    </section>
  );
}

/* ---------------- ABOUT ---------------- */
function AboutSection() {
  return (
    <section id="about" className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 scroll-mt-20">
      <p className="text-xs tracking-[0.3em] uppercase text-[#3a5a4e] mb-6">About</p>
      <h2 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tightest text-[#1a3a2e]">
        <span className="font-script text-[#8b5a2b] text-4xl md:text-5xl -rotate-2 inline-block">Hola!</span><br />
        I'm Rhea Gupta, <em className="font-light">a brand strategist,</em><br />
        influencer marketing specialist <span className="italic">&amp; founder of</span><br />
        Co Crate Studio.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={RHEA_IMG} alt="Rhea Gupta portrait" className="w-full h-full object-cover" data-testid="about-image" />
          </div>
          <p className="mt-4 font-script text-3xl text-[#8b5a2b] -rotate-2 inline-block">meet rhea.</p>
        </motion.div>
        <div className="md:col-span-7 space-y-6 text-[#3a5a4e] text-base md:text-lg leading-relaxed">
          <p>
            Over the last decade, I've worked with brands and collaborated with celebrities,
            influencers and opinion makers across lifestyle, beauty, food &amp; beverage,
            entertainment and technology.
          </p>
          <p>
            What excites me most is helping{' '}
            <strong className="text-[#1a3a2e]">brands and talent find clarity</strong> —
            understanding who they are, what they stand for, and how they can show up meaningfully
            in a crowded digital world.
          </p>
          <p>
            From <strong className="text-[#1a3a2e]">talent representation for branded collaborations</strong>{' '}
            and influencer partnerships to content strategy, campaign execution and brand building,
            I believe great marketing isn't about doing more. It's about doing the right things
            with intention.
          </p>
          <p>
            That belief led to the creation of{' '}
            <strong className="text-[#1a3a2e]">Co Crate Studio</strong> — an extended arm
            that brings strategy to life through content, campaigns and scalable systems.
          </p>
          <p>
            Today, I work with <strong className="text-[#1a3a2e]">talent, founders and brands</strong>{' '}
            to build <strong className="text-[#1a3a2e]">visibility, credibility and long-term growth</strong>{' '}
            through strategy-led content, meaningful partnerships and marketing that creates impact.
          </p>
          <p>
            No matter how platforms evolve, the goal remains the same:{' '}
            <em>building brands people remember.</em>
          </p>
        </div>
      </div>

      {/* Stats */}
    </section>
  );
}

/* ---------------- TOP CAMPAIGNS ---------------- */
function TopCampaigns() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/campaigns`)
      .then(({ data }) => {
        const list = data || [];
        const featured = list.filter((c) => c.featured);
        setItems((featured.length ? featured : list).slice(0, 12));
      })
      .catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section data-testid="top-campaigns" className="bg-[#1a3a2e] py-8 md:py-10" aria-label="Top campaigns">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between gap-4 mb-6">
          <p className="text-xs tracking-[0.3em] uppercase text-[#e3d9c6]/70">Featured Work — Top Campaigns</p>
          <Link to="/campaigns" data-testid="top-campaigns-view-all"
            className="inline-flex items-center gap-2 text-xs tracking-wide text-[#e3d9c6] hover:text-[#f5f1e8] transition-colors shrink-0">
            View all <ArrowUpRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {items.map((c, i) => (
            <TopCampaignCard key={c.id} campaign={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TopCampaignCard({ campaign, index }) {
  const embed = embedSrcFor(campaign.instagram_url);
  const cleanUrl = parseInstagramUrl(campaign.instagram_url)
    ? cleanInstagramUrl(campaign.instagram_url)
    : campaign.instagram_url;
  return (
    <motion.div
      data-testid={`top-campaign-${index}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06 }}
      className="relative bg-black border border-[#f5f1e8]/10 overflow-hidden"
    >
      <div className="relative w-full overflow-hidden bg-black aspect-[9/15]">
        {embed ? (
          <iframe
            src={embed.src}
            title={`Campaign by ${campaign.brand_name || 'Co Crate Studio'}`}
            allow="encrypted-media; clipboard-write; picture-in-picture; web-share; accelerometer; autoplay; gyroscope"
            allowFullScreen
            scrolling="no"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="border-0 absolute"
            style={embed.kind === 'instagram'
              ? { top: '-91px', left: '-30.5%', width: '237%', height: '290%', transform: 'scale(0.68)', transformOrigin: 'top left' }
              : { top: 0, left: 0, width: '100%', height: '100%' }}
          />
        ) : (
          <a href={cleanUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center w-full h-full text-[#f5f1e8]">
            <Instagram size={20} />
          </a>
        )}
      </div>
    </motion.div>
  );
}

/* ---------------- PRODUCTS BANNER ---------------- */
function ProductsBanner() {
  return (
    <section
      data-testid="products-banner"
      className="bg-[#f5f1e8] py-8 md:py-10"
      aria-label="Brand and product work"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <img
          src="/banners/products.jpg"
          alt="A collage of brand campaigns and product work"
          className="w-full h-auto block"
          loading="lazy"
          draggable="false"
        />
      </div>
    </section>
  );
}

/* ---------------- SERVICES ---------------- */
function ServicesSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="services" className="bg-[#1a3a2e] text-[#f5f1e8] py-14 md:py-16 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-xs tracking-[0.3em] uppercase text-[#e3d9c6]/70 mb-6">What We Do</p>
        <h2 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tightest">
          Creating <em className="font-light">meaningful connections</em> between
          <span className="font-script text-[#e3d9c6] text-5xl md:text-6xl -rotate-2 inline-block ml-3">brands and people.</span>
        </h2>
        <p className="mt-8 max-w-2xl text-[#e3d9c6]/85 text-lg leading-relaxed">
          Through thoughtful marketing, strategic collaborations and partnerships that create impact.
        </p>

        <div className="mt-10 border-t border-[#f5f1e8]/15">
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            return (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.04 }}
                className="border-b border-[#f5f1e8]/15">
                <button onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`service-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-7 md:py-9 text-left">
                  <div className="flex items-baseline gap-6 md:gap-10">
                    <span className="font-sans text-sm text-[#e3d9c6]/60 w-10">0{i + 1}</span>
                    <span className="font-serif text-2xl md:text-4xl text-[#f5f1e8] leading-tight">
                      {s.title}
                    </span>
                  </div>
                  <span className="shrink-0 text-[#f5f1e8]">{isOpen ? <Minus size={24} /> : <Plus size={24} />}</span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key={`desc-${i}`}
                      initial={{ gridTemplateRows: '0fr', opacity: 0 }}
                      animate={{ gridTemplateRows: '1fr', opacity: 1 }}
                      exit={{ gridTemplateRows: '0fr', opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      style={{ display: 'grid' }}
                      className="pl-0 md:pl-[80px] max-w-3xl"
                    >
                      <div className="overflow-hidden">
                        <p className="pb-8 text-[#e3d9c6]/85 text-lg leading-relaxed">
                          {s.desc}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- BATCH DROP ---------------- */
function BatchDropSection() {
  return (
    <section id="batch-drop" className="bg-[#1a3a2e] text-[#f5f1e8] pt-14 md:pt-16 pb-14 md:pb-16 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Left: title */}
        <div className="md:col-span-6">
          <p className="text-xs tracking-[0.3em] uppercase text-[#e3d9c6]/70 mb-6">Introducing</p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[1.02] text-[#f5f1e8]">
            The Batch Drop —<br />
            <em className="font-light">one shoot, 30 drops,</em><br />
            60 days of content.
          </h2>
          <p className="mt-8 max-w-md text-[#e3d9c6]/85 text-base md:text-lg leading-relaxed">
            A plug-and-play content system by Co Crate Studio. One plan, one shoot day, one final
            delivery — you just schedule and post.
          </p>
          <a href="#contact" onClick={(e) => smoothTo(e, 'contact')}
            data-testid="batch-cta"
            className="mt-10 inline-flex items-center gap-2 px-7 py-4 bg-[#f5f1e8] text-[#1a3a2e] text-sm tracking-wide hover:bg-[#e3d9c6] transition-colors">
            Build a Batch with us <ArrowUpRight size={16} />
          </a>
        </div>

        {/* Right: numbered steps with dividers */}
        <div className="md:col-span-6 md:pl-8">
          <ul className="border-t border-[#f5f1e8]/20">
            {BATCH_STEPS.map((step, i) => (
              <motion.li key={step}
                initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`batch-step-${i}`}
                className="flex items-center gap-8 md:gap-10 py-7 border-b border-[#f5f1e8]/20">
                <span className="font-serif text-3xl md:text-4xl text-[#e3d9c6]">0{i + 1}</span>
                <span className="font-sans text-base md:text-lg text-[#f5f1e8]/90">{step}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */
function ContactSection() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Got it! I'll get back to you soon.");
      setForm({ first_name: '', last_name: '', email: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Try again?');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 scroll-mt-20">
      <p className="text-xs tracking-[0.3em] uppercase text-[#3a5a4e] mb-6">Contact</p>
      <h2 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tightest text-[#1a3a2e] max-w-5xl">
        Marketing is about the <em className="font-light">stories you tell</em>
        <span className="font-script text-[#8b5a2b] text-4xl md:text-5xl -rotate-2 inline-block ml-2">&</span> experiences you create.
      </h2>
      <p className="mt-6 font-script text-4xl text-[#8b5a2b] -rotate-2 inline-block">Let's get some conversations brewing?</p>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-16">
        <form onSubmit={onSubmit} data-testid="contact-form" className="md:col-span-7 space-y-10" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Field label="First name" name="first_name" value={form.first_name} onChange={onChange} testid="contact-first-name" required />
            <Field label="Last name" name="last_name" value={form.last_name} onChange={onChange} testid="contact-last-name" />
          </div>
          <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="contact-email" required />
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">Tell us about your brand</label>
            <textarea name="message" value={form.message} onChange={onChange} rows={5}
              data-testid="contact-message" required
              placeholder="What are you working on, where you'd like to take it, and how I can help…"
              className="mt-3 block w-full bg-transparent border-b border-[#1a3a2e]/30 focus:border-[#1a3a2e] py-3 text-[#1a3a2e] placeholder:text-[#3a5a4e]/40 resize-none transition-colors" />
          </div>
          <button type="submit" data-testid="contact-submit" disabled={submitting}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#1a3a2e] text-[#f5f1e8] text-sm tracking-wide hover:bg-[#122a21] transition-colors disabled:opacity-60">
            {submitting ? 'Sending…' : 'Send'} <ArrowUpRight size={16} />
          </button>
        </form>

        <aside className="md:col-span-5 md:pl-12 md:border-l border-[#1a3a2e]/15">
          <p className="text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">Drop a hello</p>
          <div className="mt-6 space-y-5">
            <a href="mailto:hello@rheagupta.in" data-testid="contact-email-link" className="flex items-start gap-4 group">
              <Mail className="mt-1 text-[#1a3a2e]" size={18} />
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-[#3a5a4e]">Email</span>
                <span className="font-serif text-2xl text-[#1a3a2e] underline-anim">hello@rheagupta.in</span>
              </span>
            </a>
            <a href="tel:+919873542454" data-testid="contact-phone-link" className="flex items-start gap-4 group">
              <Phone className="mt-1 text-[#1a3a2e]" size={18} />
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-[#3a5a4e]">Phone</span>
                <span className="font-serif text-2xl text-[#1a3a2e] underline-anim">+91 98735 42454</span>
              </span>
            </a>
            <a href="https://instagram.com/cocratestudio" target="_blank" rel="noreferrer"
              data-testid="contact-instagram-link" className="flex items-start gap-4 group">
              <Instagram className="mt-1 text-[#1a3a2e]" size={18} />
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-[#3a5a4e]">Instagram</span>
                <span className="font-serif text-2xl text-[#1a3a2e] underline-anim">@cocratestudio</span>
              </span>
            </a>
          </div>
          <p className="mt-12 font-script text-3xl text-[#8b5a2b] -rotate-2 inline-block">
            based in Mumbai, Delhi &amp; beyond.
          </p>
        </aside>
      </div>
    </section>
  );
}

function Field({ label, name, type = 'text', value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">{label}</label>
      <input type={type} name={name} value={value} onChange={onChange} data-testid={testid} required={required}
        className="mt-3 block w-full bg-transparent border-b border-[#1a3a2e]/30 focus:border-[#1a3a2e] py-3 text-[#1a3a2e] placeholder:text-[#3a5a4e]/40 transition-colors" />
    </div>
  );
}

function smoothTo(e, id) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
